import { investigationsLabelsStore } from "Pages/Project/Investigations/Stores/InvestigationsLabelsStore";
import { observable, action, runInAction, computed, makeObservable } from "mobx";
import { API } from "General/API";
import { CoreStore, handleStoreState } from "Stores/RESTStore/CoreStore";
import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores";
import { documentsTableStore } from "../Stores/DocumentsTableStore";

export type LabelsSearchKeys = "allLabels" | "sortBy";
export type LabelsSearchConnections = "AND" | "OR";
export type LabelsSearchSortBy = "name" | "searchCount";
export type CalculatorButtonType = "AND" | "OR" | "LEFT_ARROW" | "RIGHT_ARROW" | "NOT" | "JUST" | "INHERIT";
export type CalculatorButtonValue = "&" | "(" | ")" | "|" | "!";
export interface CalculatorActionButton {
  type: CalculatorButtonType;
  name: string;
  value: CalculatorButtonValue | string;
}
export interface CustomLabelValue extends Pick<API_DataType, "labels" | "id" | "bgColour" | "fgColour"> {}
export interface CustomLabelStyle extends Pick<CustomLabelType, "color" | "backgroundColor"> {
  borderColor: string;
}
export interface CustomLabelType extends Pick<API_DataType, "viewName" | "id"> {
  color: string;
  backgroundColor: string;
}
export interface CustomRule {
  id: number;
  value: string;
}
export interface CustomResult {
  id: number;
  element: JSX.Element;
}

export type CalculatorMode = "FILE" | "FOLDER";

class LabelCalculatorStore extends CoreStore {
  initialRuleValue: string = null;
  labelsTypes: CustomLabelType[] = [];
  labelsValues: CustomLabelValue[] = [];
  labelsStyles: CustomLabelStyle = null;
  activeLabelTypeId: number = null;
  activeLabelList: API_DataLabel[] = [];
  filteredLabelList: API_DataLabel[] = [];
  activeRule: CustomRule[] = [];
  result: CustomResult[] = [];
  inputValue: string = null;
  mode: CalculatorMode = "FOLDER";
  searchOptions: { [key in LabelsSearchKeys]: LabelsSearchConnections | LabelsSearchSortBy } = {
    sortBy: "name",
    allLabels: "AND",
  };

  constructor() {
    super();

    makeObservable(this, {
      initialRuleValue: observable,
      labelsTypes: observable,
      labelsValues: observable,
      labelsStyles: observable,
      activeLabelTypeId: observable,
      activeLabelList: observable,
      filteredLabelList: observable,
      activeRule: observable,
      result: observable,
      inputValue: observable,
      mode: observable,
      clear: action.bound,
      clearResult: action.bound,
      clearRule: action.bound,
      goToFolder: action.bound,
      setStyles: action.bound,
      setList: action.bound,
      setFilteredList: action.bound,
      setTypeId: action.bound,
      setRule: action.bound,
      setResult: action.bound,
      setInputValue: action.bound,
      setMode: action.bound,
      fetchRelations: action.bound,
      fetch: action.bound,
      save: action.bound,
      saveForFile: action.bound,
      saveMultipleFiles: action.bound,
      move: action.bound,
      setSearchOptions: action.bound,
      setInitialRuleValue: action.bound,
      getRule: action.bound,
      labelSearchOptions: computed,
    });
  }

  url() {
    return API_URL.AUTO_ORGANISER.getLabelRelations(projectsDataStore.projectId);
  }

  clear() {
    this.clearResult();
    this.clearRule();
    this.initialRuleValue = null;
    this.labelsTypes = [];
    this.labelsValues = [];
    this.labelsStyles = null;
    this.activeLabelTypeId = null;
    this.activeLabelList = [];
    this.activeRule = [];
    this.filteredLabelList = [];
    this.result = [];
    this.inputValue = null;
    this.mode = "FOLDER";
    this.searchOptions = {
      sortBy: "name",
      allLabels: "AND",
    };
  }

  clearResult() {
    this.result = [];
  }

  clearRule() {
    this.activeRule = [];
  }

  @handleStoreState()
  async fetchRelations() {
    try {
      const data = await API.getData<API_DataType[]>(this.url());
      runInAction(() => {
        if (data) {
          const types = data.map(({ viewName, id, bgColour, fgColour }) => ({
            viewName,
            id,
            backgroundColor: bgColour,
            color: fgColour,
          }));
          const values = data.map(({ labels, id, bgColour, fgColour }) => ({ labels, id, bgColour, fgColour }));
          this.labelsTypes = types;
          this.labelsValues = values;
          this.setTypeId(types[0]?.id);
          this.setList(values[0]?.labels);
          this.setFilteredList(values[0]?.labels);
          this.setStyles({
            backgroundColor: types[0]?.backgroundColor,
            color: types[0]?.color,
            borderColor: types[0]?.backgroundColor,
          });
        }
      });
    } catch (error) {
      throw error;
    }
  }

  @handleStoreState()
  async fetchSearchRelations() {
    try {
      const data = await API.newData<API_DataType[]>(
        API_URL.AUTO_ORGANISER.getSortedLabelModel(projectsDataStore.projectId),
        { sortBy: this.searchOptions.sortBy }
      );
      await investigationsLabelsStore.fetchQueries();
      runInAction(() => {
        if (data) {
          const types = data.map(({ viewName, id, bgColour, fgColour }) => ({
            viewName,
            id,
            backgroundColor: bgColour,
            color: fgColour,
          }));
          const values = data.map(({ labels, id, bgColour, fgColour }) => ({ labels, id, bgColour, fgColour }));
          this.labelsTypes = types;
          this.labelsValues = values;
          this.setTypeId(types[0]?.id);
          this.setList(values[0]?.labels);
          this.setFilteredList(values[0]?.labels);
          this.setStyles({
            backgroundColor: types[0]?.backgroundColor,
            color: types[0]?.color,
            borderColor: types[0]?.backgroundColor,
          });
        }
      });
    } catch (error) {
      throw error;
    }
  }

  @handleStoreState()
  async fetch(type: "folder" | "file", id?: number) {
    try {
      await this.fetchRelations();
      if (type === "file" && id) {
        await this.fetchForFile(id);
      } else {
        await this.fetchForFolder(id);
      }
    } catch (error) {
      throw error;
    }
  }

  async fetchForFolder(id: number) {
    try {
      const targetId = id ? id : documentsTableStore.currentFolderId;
      const response = await API.getData<API_DataLabelRule>(
        API_URL.AUTO_ORGANISER.getDirectoryRules_1(projectsDataStore.projectId, targetId)
      );
      runInAction(() => {
        if (response.jsonRule) {
          this.initialRuleValue = response.jsonRule;
        }
      });
    } catch (error) {
      throw error;
    }
  }

  async fetchForFile(id: number) {
    try {
      const response = await API.getData<API_DataLabel[]>(
        API_URL.AUTO_ORGANISER.getLabelsFromFile(projectsDataStore.projectId, id)
      );
      runInAction(() => {
        if (response) {
          this.initialRuleValue = response.map((v) => v.id).join();
        }
      });
    } catch (error) {
      throw error;
    }
  }

  @handleStoreState()
  async save(id?: number) {
    try {
      const ruleValue = this.getRule(",");
      const folderId = id || documentsTableStore.currentFolderId;
      const body: API_DataLabelRule = { prologRule: ruleValue };
      if (this.initialRuleValue) {
        await API.editData<API_DataLabelRule>(
          API_URL.AUTO_ORGANISER.getDirectoryRules_1(projectsDataStore.projectId, folderId),
          body
        );
      } else {
        await API.newData<API_DataLabelRule>(
          API_URL.AUTO_ORGANISER.registerDirectoryRule(projectsDataStore.projectId, folderId),
          body
        );
      }
    } catch (error) {
      throw error;
    }
  }

  @handleStoreState()
  async saveForFile(id: number) {
    try {
      const ruleValue = this.getRule(",");
      const body: API_FileDataLabels = { labels: ruleValue };
      if (this.initialRuleValue) {
        await API.editData<API_DataLabel>(
          API_URL.AUTO_ORGANISER.changeLabelsOfFileString(projectsDataStore.projectId, id),
          body
        );
      } else {
        await API.newData<API_DataLabel>(
          API_URL.AUTO_ORGANISER.setLabelsToFileString(projectsDataStore.projectId, id),
          body
        );
      }
    } catch (error) {
      throw error;
    }
  }

  @handleStoreState()
  async saveMultipleFiles(fileIds: number[]) {
    try {
      const ruleValue = this.getRule(",");
      const body: API_FileDataLabel = { fileIds, labels: ruleValue };
      await API.newData<API_DataLabel>(API_URL.AUTO_ORGANISER.setLabelsToFile(projectsDataStore.projectId), body);
    } catch (error) {
      throw error;
    }
  }

  @handleStoreState()
  async move(files: Pick<API_FileDataEntity, "id" | "state">[], folderId: number) {
    const body: API_MultipleFileRequest = {
      files: files.map((file) => ({ id: file.id, state: file.state })),
    };
    try {
      await API.newData(API_URL.FILES.moveFiles_1(projectsDataStore.projectId, folderId), body);
    } catch (error) {
      throw error;
    }
  }

  goToFolder(folderId: number) {
    documentsTableStore.goToFolder(folderId);
  }

  setStyles({ color, backgroundColor, borderColor }: CustomLabelStyle) {
    this.labelsStyles = { color, backgroundColor, borderColor };
  }

  setList(list: API_DataLabel[]) {
    this.activeLabelList = list;
  }

  setFilteredList(list: API_DataLabel[]) {
    this.filteredLabelList = list;
  }

  setTypeId(type: number) {
    this.activeLabelTypeId = type;
  }

  setRule(newRule: CustomRule[] = []) {
    this.activeRule = newRule;
  }

  setResult(newResult: CustomResult[] = []) {
    this.result = newResult;
  }

  setInputValue(value: string) {
    this.inputValue = value;
  }

  setMode(value?: CalculatorMode) {
    this.mode = value || "FOLDER";
  }

  setSearchOptions(key: LabelsSearchKeys, value: LabelsSearchConnections | LabelsSearchSortBy) {
    this.searchOptions[key] = value;
  }

  setInitialRuleValue(rule: string) {
    this.initialRuleValue = rule;
  }

  getRule(separator: string = "") {
    return this.activeRule.map(({ value }) => value).join(separator);
  }

  get labelSearchOptions() {
    return this.searchOptions;
  }
}

const labelCalculatorStore = new LabelCalculatorStore();

export { labelCalculatorStore, LabelCalculatorStore };
